import React, { useState, useEffect } from "react";
import { Card, Row, Col, Table, Badge, Button, Alert, Spinner } from "react-bootstrap";
import axiosInstance from "../../api/axiosRest";
import { PAYMENT, REFERRAL } from "../../api/urls";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './payoutDashboard.scss';

export default function PayoutDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard data
      const dashboardRes = await axiosInstance.get(REFERRAL.DASHBOARD);
      setDashboardData(dashboardRes.data);
      
      // Fetch detailed payout history
      const payoutRes = await axiosInstance.get(REFERRAL.PAYOUT_HISTORY);
      setPayoutHistory(payoutRes.data);
      
    } catch (error) {
      console.error("Error loading payout data:", error);
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };
  
  const handleConnectAccount = async () => {
    try {
      const response = await axiosInstance.post(PAYMENT.CREATE_CONNECTED_ACCOUNT);
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.account_complete) {
        toast.info("Your payout account is already fully set up!");
        fetchData(); // Refresh dashboard data
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Connect account error:", error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Failed to create connected account");
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    return status === 'paid' ? 
      <Badge bg="success">Paid</Badge> : 
      <Badge bg="warning">Pending</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!dashboardData) {
    return <Alert variant="danger">Failed to load dashboard data</Alert>;
  }

  const { earnings_summary, connect_account, commissions } = dashboardData;

  return (
    <div className="payout-dashboard">
      <ToastContainer />
      
      {/* Connect Account Status */}
      {!connect_account.onboarding_complete && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Setup Required</Alert.Heading>
          <p>
            You need to complete your Stripe Connect account setup to receive cash payouts.
            {connect_account.connected 
              ? " Your account exists but needs to complete onboarding. Click below to continue." 
              : " Click the button below to get started."
            }
          </p>
          <Button variant="primary" onClick={handleConnectAccount}>
            {connect_account.connected ? "Complete Setup" : "Setup Payout Account"}
          </Button>
        </Alert>
      )}

      {/* Earnings Summary */}
      <Row className="mb-4 earnings-summary-row">
        <Col md={3} sm={6} xs={12} className="earnings-col">
          <Card className="text-center earnings-card">
            <Card.Body>
              <Card.Title className="text-success">
                ${earnings_summary.total_cash_earned}
              </Card.Title>
              <Card.Text>Total Cash Earned</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} xs={12} className="earnings-col">
          <Card className="text-center earnings-card">
            <Card.Body>
              <Card.Title className="text-warning">
                ${earnings_summary.pending_cash}
              </Card.Title>
              <Card.Text>Pending Payouts</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} xs={12} className="earnings-col">
          <Card className="text-center earnings-card">
            <Card.Body>
              <Card.Title className="text-info">
                {earnings_summary.total_referrals}
              </Card.Title>
              <Card.Text>Total Referrals</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} xs={12} className="earnings-col">
          <Card className="text-center earnings-card">
            <Card.Body>
              <Card.Title className="text-secondary">
                ${earnings_summary.total_credits_earned}
              </Card.Title>
              <Card.Text>Credits Earned</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Payouts */}
      <Card>
        <Card.Header>
          <h5>Recent Payouts</h5>
        </Card.Header>
        <Card.Body>
          {commissions.length === 0 ? (
            <p id="recent-payouts-card" className="text-muted">No payouts yet. Start referring users to earn cash!</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Referee</th>
                  <th>Transfer ID</th>
                </tr>
              </thead>
              <tbody>
                {commissions.slice(0, 10).map((commission) => (
                  <tr key={commission.id}>
                    <td>{formatDate(commission.created_at)}</td>
                    <td className="fw-bold">
                      ${commission.amount}
                    </td>
                    <td>
                      <Badge bg={commission.payment_type === 'cash' ? 'primary' : 'secondary'}>
                        {commission.payment_type}
                      </Badge>
                    </td>
                    <td>
                      {getStatusBadge(commission.is_paid ? 'paid' : 'pending')}
                    </td>
                    <td>{commission.referee_username || 'N/A'}</td>
                    <td>
                      {commission.stripe_transfer_id ? (
                        <small className="text-muted">
                          {commission.stripe_transfer_id.substring(0, 20)}...
                        </small>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Detailed Payout History */}
      {payoutHistory && payoutHistory.payout_history.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <h5>Cash Payout History</h5>
            <small className="text-muted">
              {payoutHistory.summary.total_payouts} completed payouts • 
              {payoutHistory.summary.pending_payouts} pending
            </small>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Referee</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {payoutHistory.payout_history.map((payout) => (
                  <tr key={payout.id}>
                    <td>{formatDate(payout.created_at)}</td>
                    <td className="fw-bold text-success">${payout.amount}</td>
                    <td>{getStatusBadge(payout.status)}</td>
                    <td>{payout.referee_username}</td>
                    <td>
                      {payout.transfer_details?.description || 'Referral payout'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Help Text */}
      <Alert variant="info" className="mt-4">
        <Alert.Heading>How Payouts Work</Alert.Heading>
        <ul className="mb-0">
          <li><strong>Monthly Plans:</strong> Earn $8 every month when your referral's subscription renews</li>
          <li><strong>Yearly Plans:</strong> Earn $60 upfront when your referral subscribes</li>
          <li><strong>Automatic Payouts:</strong> Money is sent directly to your bank account via Stripe</li>
          <li><strong>Processing Time:</strong> Payouts typically arrive within 2-7 business days</li>
        </ul>
      </Alert>
    </div>
  );
} 